import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { Link, useNavigate } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, DollarSign, Activity, RefreshCw, ExternalLink, Lock } from 'lucide-react';

export default function SuperAdminBetaFeatures() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [gates, setGates] = useState({ ai_event_drafting_mode: 'closed_beta', ai_event_drafting_mode_options: [] });
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [reportRes, gatesRes] = await Promise.all([
        authAxios.get(`${API}/platform/beta-features/ai-draft-cost-report`),
        authAxios.get(`${API}/platform/feature-gates`),
      ]);
      setReport(reportRes.data);
      setGates(gatesRes.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const updateMode = async (newMode) => {
    setSavingMode(true);
    try {
      const { data } = await authAxios.patch(`${API}/platform/feature-gates`, {
        ai_event_drafting_mode: newMode,
      });
      setGates(data);
      toast.success(`Feature gate updated → ${newMode.replace('_', ' ')}`);
      if (newMode === 'unavailable') {
        toast.info('All per-league AI flags have been auto-disabled.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Link to="/super-admin" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3 h-3" /> Back to Dashboard
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              Beta Features
            </h1>
            <p className="text-slate-600 text-sm">Adoption + cost tracking across all leagues.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="beta-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Platform Feature Gate */}
        <Card data-testid="ai-feature-gate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-4 h-4 text-slate-600" />
              Feature Gate — AI Event Drafting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-700">
              Control whether League Admins can see and toggle the AI Event Drafting feature on their leagues.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Select
                value={gates.ai_event_drafting_mode}
                onValueChange={updateMode}
                disabled={savingMode || loading}
              >
                <SelectTrigger className="w-full sm:w-64" data-testid="feature-gate-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unavailable">Unavailable — hidden everywhere</SelectItem>
                  <SelectItem value="closed_beta">Closed Beta — Super Admin only</SelectItem>
                  <SelectItem value="open_beta">Open Beta — any League Admin</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-500">
                Current: <strong>{gates.ai_event_drafting_mode?.replace('_', ' ')}</strong>
              </span>
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              <div><strong>Unavailable</strong>: No one sees the feature. All per-league flags auto-disable.</div>
              <div><strong>Closed Beta</strong>: League Admins don't see the toggle. Only you (impersonating a tenant or via tenant context) can flip it per league.</div>
              <div><strong>Open Beta</strong>: League Admins see the toggle on /admin/league-options and can self-enable.</div>
            </div>
          </CardContent>
        </Card>

        {/* AI Event Drafting summary */}
        <Card data-testid="ai-draft-cost-summary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              AI Event Drafting
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">BETA</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
            {!loading && report && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 rounded-md p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total Runs</div>
                  <div className="text-2xl font-bold text-slate-800 mt-1 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    {report.total_runs}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-md p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Estimated Total Cost</div>
                  <div className="text-2xl font-bold text-slate-800 mt-1 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    ${Number(report.total_cost_usd || 0).toFixed(4)}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Approximate (chars/4)</div>
                </div>
                <div className="bg-slate-50 rounded-md p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Leagues Using It</div>
                  <div className="text-2xl font-bold text-slate-800 mt-1">{report.by_tenant?.length || 0}</div>
                </div>
              </div>
            )}

            {!loading && report?.by_tenant?.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">By League</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                      <th className="py-2 pr-4">League</th>
                      <th className="py-2 pr-4 text-right">Runs</th>
                      <th className="py-2 pr-4 text-right">Drafts Created</th>
                      <th className="py-2 pr-4 text-right">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_tenant.map((t) => (
                      <tr key={t.tenant_id} className="border-b last:border-b-0">
                        <td className="py-2 pr-4">{t.tenant_name}</td>
                        <td className="py-2 pr-4 text-right">{t.runs}</td>
                        <td className="py-2 pr-4 text-right">{t.drafts_created}</td>
                        <td className="py-2 pr-4 text-right">${Number(t.total_cost_usd || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && report?.recent_runs?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Recent Runs (last 50)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left uppercase text-slate-500 border-b">
                        <th className="py-2 pr-4">Started</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Country</th>
                        <th className="py-2 pr-4 text-right">Drafts</th>
                        <th className="py-2 pr-4 text-right">Skipped</th>
                        <th className="py-2 pr-4 text-right">Est. Cost</th>
                        <th className="py-2 pr-4">Sources</th>
                        <th className="py-2 pr-4">Trigger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.recent_runs.map((r) => (
                        <tr key={r.id} className="border-b last:border-b-0">
                          <td className="py-2 pr-4 text-slate-600">{(r.started_at || '').slice(0, 19).replace('T', ' ')}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              r.status === 'completed' ? 'bg-emerald-100 text-emerald-800'
                              : r.status === 'failed' ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                            }`}>{r.status}</span>
                          </td>
                          <td className="py-2 pr-4">{r.country_name || '—'}</td>
                          <td className="py-2 pr-4 text-right">{r.drafts_created || 0}</td>
                          <td className="py-2 pr-4 text-right">{r.events_skipped || 0}</td>
                          <td className="py-2 pr-4 text-right">${Number(r.estimated_cost_usd || 0).toFixed(4)}</td>
                          <td className="py-2 pr-4">
                            {Array.isArray(r.source_urls) && r.source_urls.length > 0 ? (
                              <a href={r.source_urls[0]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-purple-600 hover:underline">
                                {r.source_urls.length} <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : '0'}
                          </td>
                          <td className="py-2 pr-4 text-slate-500">{r.triggered_by || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && (!report || report.total_runs === 0) && (
              <div className="text-sm text-slate-500 py-8 text-center">
                No AI draft runs yet. Once a league enables the feature, runs will appear here.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
